import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react"
import { FormikProps } from "formik"
import {
  useDeleteIntegrationMutation,
  useSaveIntegrationMutation
} from "../../../../redux/queries"
import { IntegrationStatus, Integration } from "../../../../types"
import { useToast } from "../../../hooks"
import { normalizeUrl } from "../../../utils"
import BBDialog from "../../dialog"
import CreateIntegration from "./create"
import EditIntegration from "./edit"

export const IntegrationContext = createContext<any>({})

type Props = {
  open: boolean
  isCreate?: boolean
  integrationId?: string
  integration?: Integration
  statusData?: IntegrationStatus[]
  isLoadingStatus?: boolean
  isLoadingIntegration?: boolean
  onClose: () => void
}

const ManageIntegration = ({
  open,
  isCreate = true,
  integrationId,
  integration,
  statusData,
  isLoadingStatus = false,
  isLoadingIntegration = false,
  onClose
}: Props) => {
  const [deleteIntegration, { isSuccess, isError }] =
    useDeleteIntegrationMutation()

  const [
    saveIntegration,
    { reset, isSuccess: isSaveSuccess, isError: isSaveError }
  ] = useSaveIntegrationMutation()

  const formIntegrationRef = useRef<FormikProps<Integration>>(null)
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false)

  const openDeleteModal = () => setDeleteModalVisible(true)
  const closeDeleteModal = () => setDeleteModalVisible(false)

  const onSubmitIntegration = useCallback(
    () => formIntegrationRef.current?.submitForm(),
    []
  )

  const handleDeleteIntegration = async () => {
    if (!isCreate && integration?.id) {
      const res = await deleteIntegration(integration?.id)
      if ("data" in res) {
        onClose()
      }
      closeDeleteModal()
    }
  }

  const { pushSuccessToast, pushErrorToast } = useToast()

  useEffect(() => {
    if (isSuccess) {
      pushSuccessToast("Integration has been deleted successfully")
    }
    if (isError) {
      pushErrorToast("Failed to delete integration")
    }
  }, [pushSuccessToast, pushErrorToast, isError, isSuccess])

  const onSaveSettings = useCallback(
    (values: Integration) => {
      const {
        id,
        owner,
        url,
        title,
        type,
        accent_color,
        secondary_accent_color,
        app_settings
      } = values

      const {
        mort_calc = 0,
        afford_calc = 0,
        lead_workflow,
        pricing_set_id,
        settings,
        rate_table_config
      } = app_settings ?? {}

      const pricing: 1 | 0 = app_settings?.pricing === 1 ? 1 : 0

      const { rateCardsConfigId } = settings ?? {}

      const isValidHexColor = (color?: string): boolean => {
        if (!color) return false
        return /^#([0-9A-F]{3}){1,2}$/i.test(color)
      }

      const normalizedAccentColor = accent_color || settings?.accent_color || ""
      const normalizedSecondaryColor =
        secondary_accent_color || settings?.secondary_accent_color || ""

      const postValues: any = {
        id,
        url: normalizeUrl(url),
        owner,
        title,
        type,
        mort_calc,
        afford_calc,
        lead_workflow,
        pricing,
        pricing_set_id,
        aboutBoxes: settings?.aboutBoxes ?? false,
        expose_api: settings?.expose_api ?? true,
        disableCalcCards: settings?.disableCalcCards ?? false,
        postUrl: settings?.postUrl ?? "",
        loReviews: settings?.loReviews || false,
        brandReviews: settings?.brandReviews ?? false,
        rateTable: settings?.rateTable ?? false,
        rateCards: settings?.rateCards ?? false,
        customParams: settings?.customParams ?? undefined,
        ga_tag: settings?.ga_tag ?? "",
        ga_mode: settings?.ga_mode ?? "",
        contactForm: settings?.contactForm ?? 0,
        rate_table_config: rate_table_config ?? "",
        rateCardsConfigId: rateCardsConfigId ?? "",
        billing_exempt: settings?.billing_exempt ?? false
      }

      if (isValidHexColor(normalizedAccentColor)) {
        postValues.accent_color = normalizedAccentColor
      }
      if (isValidHexColor(normalizedSecondaryColor)) {
        postValues.secondary_accent_color = normalizedSecondaryColor
      }

      if (!id) {
        delete postValues.id
      }

      saveIntegration(postValues)
    },
    [saveIntegration]
  )

  useEffect(() => {
    if (isSaveSuccess) {
      pushSuccessToast(
        `Integration has been ${
          integration?.id ? "updated" : "created"
        } successfully`
      )
      reset()
      onClose()
    } else if (isSaveError) {
      pushErrorToast(
        `Failed to ${integration?.id ? "updated" : "created"} integration`
      )
    }
  }, [
    integration?.id,
    isSaveSuccess,
    isSaveError,
    onClose,
    reset,
    pushErrorToast,
    pushSuccessToast
  ])

  // Prevent stale cached data: only pass integration when the loaded ID matches expected.
  const integrationIdStr = String(integrationId ?? "")
  const loadedIdStr = String(integration?.id ?? "")
  const isIdMatch =
    !!integration && !!integrationId && loadedIdStr === integrationIdStr

  // Remember last matched integration so the panel hides with real content (not skeleton).
  const lastMatchedIntegrationRef = useRef<Integration | undefined>(undefined)
  if (isIdMatch) lastMatchedIntegrationRef.current = integration

  const resolvedIntegration = isIdMatch
    ? integration
    : lastMatchedIntegrationRef.current
  const resolvedIsLoading =
    isLoadingIntegration || (open && !!integrationId && !isIdMatch)

  // Keep EditIntegration mounted after close so SidePanel's leave animation can play.
  // Once integrationId is set, we don't unmount until a full remount (e.g. create mode).
  const lastIntegrationIdRef = useRef<string | undefined>(undefined)
  if (integrationId) lastIntegrationIdRef.current = integrationId

  const shouldRenderEditIntegration =
    !isCreate && !!lastIntegrationIdRef.current

  return (
    <IntegrationContext.Provider value={{ formIntegrationRef }}>
      {isCreate && !isLoadingIntegration && (
        <CreateIntegration
          open={open}
          onClose={onClose}
          onSubmitIntegration={onSubmitIntegration}
          onSaveSettings={onSaveSettings}
        />
      )}
      {shouldRenderEditIntegration && (
        <EditIntegration
          integration={resolvedIntegration}
          isLoadingIntegration={resolvedIsLoading}
          statusData={statusData}
          isLoadingStatus={isLoadingStatus}
          open={open}
          onClose={onClose}
          onSubmitIntegration={onSubmitIntegration}
          openDeleteModal={openDeleteModal}
          onSaveSettings={onSaveSettings}
        />
      )}
      <BBDialog
        open={deleteModalVisible}
        onClose={closeDeleteModal}
        title="Delete integration"
        body="Are you sure you want to delete this integration?"
        okLabel="Yes, Delete"
        cancelLabel="Cancel"
        onOk={handleDeleteIntegration}
        onCancel={closeDeleteModal}
      />
    </IntegrationContext.Provider>
  )
}

const useFormIntegrationRef = () => {
  const { formIntegrationRef } = useContext(IntegrationContext)
  return formIntegrationRef
}

export default ManageIntegration

export { useFormIntegrationRef }
